import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Button,
  Card,
  DataDisplay,
  formatBoolean,
  formatDate,
  formatSchoolYear,
  Grid,
  GridBreak,
  pn,
  Stack,
  TextField,
  Typography,
  useElementBreakpoints,
} from "@compass/core";
import { DatePicker, Select } from "components";
import {
  AprUbResponseRow,
  Permission,
  PermissionAction,
  PostsecondaryEducation,
  StudentAprUbResource,
  Student,
  StudentEditResource,
  StudentEnrollment,
  StudentEnrollmentEditResource,
} from "generated/openApi";
import "moment-timezone";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ReactNode, useEffect, useRef, useState } from "react";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import produce from "immer";
import { studentEnrollmentPageRoute } from "../../student-enrollments/StudentEnrollmentPage/StudentEnrollmentPage";
import { schoolPageRoute } from "../../../system/utility/schools/SchoolPage/SchoolPage";
import { useRouter } from "hooks";

export const StudentAprUb = (props: {
  initialLoad: boolean;
  studentId: number;
  refreshKey?: number;
  editPostSecondary: () => void;
  editFafsa: () => void;
  editServices: () => void;
  editGrades: () => void;
  editGpas: () => void;
  onMissingAprDataChange: (hasMissingAprData: boolean) => void;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, mediumFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();

  // const navigate = useNavigate();
  // const { matchTemplatePath } = useRouter();
  const [apr, setApr] = useState<AprUbResponseRow>({} as AprUbResponseRow);
  const [resource, setResource] = useState<StudentAprUbResource>({} as StudentAprUbResource);
  const [selectedSchoolyear, setSelectedSchoolyear] = useState<number>();
  const [schoolYears, setSchoolYears] = useState<number[]>([]);
  const [studentEnrollments, setStudentEnrollments] = useState<StudentEnrollment[]>([]);
  const [student, setStudent] = useState<Student>({} as Student);
  const [studentEditResource, setStudentEditResource] = useState<StudentEditResource>({} as StudentEditResource);
  const [enrollmentEditResource, setEnrollmentEditResource] = useState<StudentEnrollmentEditResource>({});
  const [editingSection, setEditingSection] = useState<"demographics" | "eligibility" | "participation" | "services" | "postsecondary">();
  const [studentDraft, setStudentDraft] = useState<Student>({} as Student);
  const [enrollmentDraft, setEnrollmentDraft] = useState<StudentEnrollment>();
  const [firstEnrollmentDraft, setFirstEnrollmentDraft] = useState<StudentEnrollment>();
  const [postsecondaryEducation, setPostsecondaryEducation] = useState<PostsecondaryEducation>();
  const [postsecondaryEducationDraft, setPostsecondaryEducationDraft] = useState<PostsecondaryEducation>();
  const [, setMissingAprData] = useState<string[]>([]);
  const aprProperties = useRef<StudentAprUbResource["properties"]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { matchTemplatePath } = useRouter();

  const setAprData = (
    response: AprUbResponseRow,
    propertiesToCheck = aprProperties.current ?? []
  ) => {
    setApr(response);

    const missingData = propertiesToCheck
      .map((property, index) => ({ property, fieldNumber: index + 1 }))
      .filter(({ property }) => {
        if (
          property.propertyName === pn<AprUbResponseRow>("middleInitial") ||
          property.propertyName === pn<AprUbResponseRow>("fullNameChange") ||
          property.propertyName === pn<AprUbResponseRow>("program")
        ) return false;

        const value = response[property.propertyName as keyof AprUbResponseRow];
        if (property.propertyName === pn<AprUbResponseRow>("participantStatus")) {
          return value === 2 || value === 3 || value === 4;
        }

        return value === undefined || value === null || value === "";
      })
      .map(({ property, fieldNumber }) => {
        const fieldName = property.displayName ?? property.propertyName;
        return fieldName ? `${fieldNumber}.${fieldName}` : undefined;
      })
      .filter((name): name is string => Boolean(name));

    setMissingAprData(missingData);
    props.onMissingAprDataChange(missingData.length > 0);
  };

  useEffect(() => {
    if (props.initialLoad) {
      fetchData();
    }
  }, [props.initialLoad, props.studentId]);

  useEffect(() => {
    if (selectedSchoolyear) {
      ApiService.students.getAprUb(props.studentId, selectedSchoolyear).then((response) => {
        setAprData(response);
      });
      fetchPostsecondaryEducation(selectedSchoolyear);
    }
  }, [selectedSchoolyear]);

  useEffect(() => {
    if (props.initialLoad && selectedSchoolyear) {
      ApiService.students.getAprUb(props.studentId, selectedSchoolyear).then((response) => {
        setAprData(response);
      });
    }
  }, [props.refreshKey]);

  useEffect(() => {
    if (props.initialLoad && selectedSchoolyear) {
      refreshApr();
    }
  }, [location.key]);

  const fetchData = async () => {

    const ubResource = await ApiService.students.getAprUbResource();
    if (ubResource) {
      aprProperties.current = ubResource.properties;
      setResource(ubResource);
    }

    const [studentResponse, studentResource, enrollmentResource] = await Promise.all([
      ApiService.students.get(props.studentId),
      ApiService.students.getEditResource(),
      ApiService.studentEnrollments.getEditResource(),
    ]);
    setStudent(studentResponse);
    setStudentEditResource(studentResource);
    setEnrollmentEditResource(enrollmentResource);

    const studentEnrollments = await ApiService.studentEnrollments.getEnrollmentsByStudentId(props.studentId);
    if (studentEnrollments && studentEnrollments.length > 0) {
      setStudentEnrollments(studentEnrollments);
      const years = studentEnrollments.map((enrollment) => enrollment.year);
      setSchoolYears(years);
      setSelectedSchoolyear(years[0]);
    } else {
      setMissingAprData(["School Year"]);
      props.onMissingAprDataChange(true);
    }
  };

  const properties = resource.properties || [];

  const getEnrollment = (useFirstEnrollment: boolean = false) => {
    const enrollments = studentEnrollments
      .filter((enrollment) => enrollment.year === selectedSchoolyear)
      .sort((a, b) => new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime());
    const enrollment = useFirstEnrollment ? enrollments[0] : enrollments[enrollments.length - 1];
    return enrollment;
  };

  const getFirstEverEnrollment = () =>
    [...studentEnrollments].sort((a, b) => new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime())[0];

  const getNextYearEnrollment = () =>
    studentEnrollments
      .filter((enrollment) => enrollment.year === (selectedSchoolyear ?? 0) + 1)
      .sort((a, b) => new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime())[0];

  const getGraduatingEnrollment = () =>
    studentEnrollments.find((enrollment) => [5, 8, 9].includes(enrollment.endReason?.endReasonTypeId ?? 0));

  const beginStudentEdit = (section: "demographics" | "eligibility") => {
    setStudentDraft(produce(student, () => {}));
    setEditingSection(section);
  };

  const beginEnrollmentEdit = (section: "participation" | "services") => {
    const enrollment = getEnrollment();
    if (!enrollment) return;
    setEnrollmentDraft(produce(enrollment, () => {}));
    setFirstEnrollmentDraft(produce(getEnrollment(true) ?? enrollment, () => {}));
    setStudentDraft(produce(student, () => {}));
    setEditingSection(section);
  };

  const cancelEdit = () => {
    setEditingSection(undefined);
    setEnrollmentDraft(undefined);
    setFirstEnrollmentDraft(undefined);
    setPostsecondaryEducationDraft(undefined);
  };

  const refreshApr = async () => {
    if (!selectedSchoolyear) return;
    setAprData(await ApiService.students.getAprUb(props.studentId, selectedSchoolyear));
  };

  const fetchPostsecondaryEducation = async (schoolYear: number) => {
    setPostsecondaryEducation(await ApiService.students.getPostsecondaryEducation(props.studentId, schoolYear));
  };

  const beginPostsecondaryEducationEdit = () => {
    if (!postsecondaryEducation) return;
    setPostsecondaryEducationDraft(produce(postsecondaryEducation, draft => {
      draft.psecdfe ??= apr.pseCdfe === undefined ? undefined : Number(apr.pseCdfe);
      draft.pseGradeLV ??= apr.pseGradeLv;
      draft.certificateCD ??= apr.certificateCd;
      draft.certificateDT ??= apr.certificateDt;
      draft.assocDegreeCD ??= apr.assocDegreeCd;
      draft.assocDegreeDT ??= apr.assocDegreeDt;
      draft.bachDegreeCD ??= apr.bachDegreeCd;
      draft.bachDegreeDT ??= apr.bachDegreeDt;
      draft.pseEnrollObjNum ??= apr.pseEnrollObjNum;
      draft.psRemediation ??= apr.psRemediation;
      draft.psCompleteNum ??= apr.psCompleteNum;
      draft.stemDegree ??= apr.stemDegree;
    }));
    setEditingSection("postsecondary");
  };

  const savePostsecondaryEducation = async () => {
    if (!postsecondaryEducationDraft) return;
    const id = await ApiService.students.savePostsecondaryEducation(postsecondaryEducationDraft);
    setPostsecondaryEducation({ ...postsecondaryEducationDraft, id });
    setPostsecondaryEducationDraft(undefined);
    setEditingSection(undefined);
    await refreshApr();
  };

  const saveStudent = async () => {
    await ApiService.students.saveUpdate(studentDraft);
    setStudent(studentDraft);
    cancelEdit();
    await refreshApr();
  };

  const saveEnrollment = async () => {
    if (!enrollmentDraft) return;
    const enrollmentsToSave = firstEnrollmentDraft && firstEnrollmentDraft.id !== enrollmentDraft.id
      ? [enrollmentDraft, firstEnrollmentDraft]
      : [enrollmentDraft];
    const saves = enrollmentsToSave.map((enrollment) => ApiService.studentEnrollments.saveUpdate(enrollment));
    if (editingSection === "participation") {
      saves.push(ApiService.students.saveUpdate(studentDraft));
    }
    await Promise.all(saves);
    if (editingSection === "participation") {
      setStudent(studentDraft);
    }
    setStudentEnrollments((current) => current.map((x) => enrollmentsToSave.find((saved) => saved.id === x.id) ?? x));
    cancelEdit();
    await refreshApr();
  };

  const editEnrollment = (_useFirstEnrollment: boolean = false) => beginEnrollmentEdit("participation");

  const openEnrollmentEditor = (useFirstEnrollment: boolean = false) => {
    const enrollment = getEnrollment(useFirstEnrollment);
    openEnrollment(enrollment);
  };

  const openEnrollment = (enrollment?: StudentEnrollment) => {
    if (!enrollment) return;
    navigate(studentEnrollmentPageRoute.getPath({ id: enrollment.id, crudMode: "edit", screenType: "dialog" }));
  };

  const openEndGradeEditor = () => {
    const nextYearEnrollment = getNextYearEnrollment();
    if (nextYearEnrollment) {
      openEnrollment(nextYearEnrollment);
      return;
    }

    props.editPostSecondary();
  };

  const openSchoolEditor = () => {
    const schoolId = getEnrollment()?.schoolId;
    if (schoolId) navigate(schoolPageRoute.getPath({ id: schoolId, crudMode: "edit", screenType: "dialog" }));
  };

  const editableField = (field: ReactNode, edit: () => void, permission: Permission) => <>{field}</>;

  const linkedField = (
    field: ReactNode,
    section: "eligibility" | "participation",
    edit: () => void,
    permission: Permission
  ) => (
    <Stack direction="row" alignItems="center" spacing="xs">
      <Box flexGrow={1}>{field}</Box>
      {editingSection === section && isAuthorized(permission, PermissionAction.Update) && (
        <IconButton size="small" aria-label="Edit source information" onClick={edit}><EditIcon fontSize="small" /></IconButton>
      )}
    </Stack>
  );

  // const properties = resource.properties || [];

  const eligibilityField = () => {
    const propertyName = pn<AprUbResponseRow>("eligibility");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(<DataDisplay
      // label={property?.displayName}
      label="16.EligibilityCD"
    >{apr.eligibility}</DataDisplay>, () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const genderCodeField = () => {
    const propertyName = pn<AprUbResponseRow>("genderCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`9.${property?.displayName}`}>{apr.genderCode}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const dateOfBirthField = () => {
    const propertyName = pn<AprUbResponseRow>("dateOfBirth");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`8.${property?.displayName}`}>{formatDate(apr.dateOfBirth)}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const ethnicityField = () => {
    const propertyName = pn<AprUbResponseRow>("ethnicityCode");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`10.${property?.displayName}`}>{apr.ethnicityCode}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const isRaceNativeField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceNative");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`11.${property?.displayName}`}>{apr.isRaceNative}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const isRaceAsianField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceAsian");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`12.${property?.displayName}`}>{apr.isRaceAsian}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const isRaceAfricanField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceAfrican");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`13.${property?.displayName}`}>{apr.isRaceAfrican}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const isRaceWhiteField = () => {
    const propertyName = pn<AprUbResponseRow>("isRaceWhite");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`14.${property?.displayName}`}>{apr.isRaceWhite}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const isRacePacificField = () => {
    const propertyName = pn<AprUbResponseRow>("isRacePacific");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`15.${property?.displayName}`}>{apr.isRacePacific}</DataDisplay>,
      () => beginStudentEdit("demographics"),
      Permission.Student
    );
  };

  const lepEntryField = () => {
    const propertyName = pn<AprUbResponseRow>("lepEntry");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`20.${property?.displayName}`}>{apr.lepEntry}</DataDisplay>,
      "eligibility", () => openEnrollment(getFirstEverEnrollment()), Permission.StudentEnrollment
    );
  };

  const lepInstructionField = () => {
    const propertyName = pn<AprUbResponseRow>("lepInstruction");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`51.${property?.displayName}`}>{apr.lepInstruction}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const deceasedField = () => {
    const propertyName = pn<AprUbResponseRow>("deceased");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`26.${property?.displayName}`}>{apr.deceased}</DataDisplay>,
      () => beginEnrollmentEdit("participation"),
      Permission.Student
    );
  };

  const ncesSchoolField = () => {
    const propertyName = pn<AprUbResponseRow>("ncesSchool");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`23.${property?.displayName}`}>{apr.ncesSchool}</DataDisplay>,
      "participation", openSchoolEditor, Permission.UtilitySchools
    );
  };

  const collegeGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("collegeGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`25.${property?.displayName}`}>{apr.collegeGradeLevel}</DataDisplay>,
      "participation", () => openEnrollment(getFirstEverEnrollment()), Permission.StudentEnrollment
    );
  };

  const projectEntryDateField = () => {
    const propertyName = pn<AprUbResponseRow>("projectEntryDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`24.${property?.displayName}`}>{apr.projectEntryDate}</DataDisplay>,
      "participation", props.editServices, Permission.StudentService
    );
  };

  const participantStatusField = () => {
    const propertyName = pn<AprUbResponseRow>("participantStatus");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`27.${property?.displayName}`}>{apr.participantStatus}</DataDisplay>,
      "participation", () => openEnrollmentEditor(true), Permission.StudentEnrollment
    );
  };

  const participantLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("participantLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`28.${property?.displayName}`}>{apr.participantLevel}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const federalServedField = () => {
    const propertyName = pn<AprUbResponseRow>("federalServed");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`29.${property?.displayName}`}>{apr.federalServed}</DataDisplay>,
      "participation", () => openEnrollmentEditor(true), Permission.StudentEnrollment
    );
  };

  const startGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("startGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`30.${property?.displayName}`}>{apr.startGradeLevel}</DataDisplay>,
      () => editEnrollment(true),
      Permission.StudentEnrollment
    );
  };

  const endGradeLevelField = () => {
    const propertyName = pn<AprUbResponseRow>("endGradeLevel");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(
      <DataDisplay label={`31.${property?.displayName}`}>{apr.endGradeLevel}</DataDisplay>,
      "participation", openEndGradeEditor, Permission.StudentEnrollment
    );
  };

  const highSchoolGPAField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGPA");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`32.${property?.displayName}`}>{apr.highSchoolGPA}</DataDisplay>, "participation", props.editGpas, Permission.StudentGpa);
  };

  const highSchoolGPAUnweightedField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGPAUnweighted");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`33.${property?.displayName}`}>{apr.highSchoolGPAUnweighted}</DataDisplay>, "participation", props.editGpas, Permission.StudentGpa);
  };

  const highSchoolGraduateField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGraduate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`34.${property?.displayName}`}>{apr.highSchoolGraduate}</DataDisplay>, "participation", () => openEnrollment(getGraduatingEnrollment()), Permission.StudentEnrollment);
  };

  const highSchoolGraduateDateField = () => {
    const propertyName = pn<AprUbResponseRow>("highSchoolGraduateDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`35.${property?.displayName}`}>{apr.highSchoolGraduateDate}</DataDisplay>, "participation", () => openEnrollment(getGraduatingEnrollment()), Permission.StudentEnrollment);
  };

  const rigorousStudyField = () => {
    const propertyName = pn<AprUbResponseRow>("rigorousStudy");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`36.${property?.displayName}`}>{apr.rigorousStudy}</DataDisplay>, "participation", () => openEnrollment(getGraduatingEnrollment()), Permission.StudentEnrollment);
  };

  const hsProficientRlaField = () => {
    const propertyName = pn<AprUbResponseRow>("hsProficientRla");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`38.${property?.displayName}`}>{apr.hsProficientRla}</DataDisplay>, "participation", () => openEnrollment(getGraduatingEnrollment()), Permission.StudentEnrollment);
  };

  const hsProficientMathField = () => {
    const propertyName = pn<AprUbResponseRow>("hsProficientMath");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`39.${property?.displayName}`}>{apr.hsProficientMath}</DataDisplay>, "participation", () => openEnrollment(getGraduatingEnrollment()), Permission.StudentEnrollment);
  };

  const apCourseField = () => {
    const propertyName = pn<AprUbResponseRow>("apCourse");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`40.${property?.displayName}`}>{apr.apCourse}</DataDisplay>, "participation", props.editGrades, Permission.StudentGrade);
  };

  const dualEnrollParticipationField = () => {
    const propertyName = pn<AprUbResponseRow>("dualEnrollParticipation");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`41.${property?.displayName}`}>{apr.dualEnrollParticipation}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const dualEnrollCredentials = () => {
    const propertyName = pn<AprUbResponseRow>("dualEnrollCredentials");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`42.${property?.displayName}`}>{apr.dualEnrollCredentials}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const advancedMathField = () => {
    const propertyName = pn<AprUbResponseRow>("advancedMath");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`43.${property?.displayName}`}>{apr.advancedMath}</DataDisplay>, "participation", props.editGrades, Permission.StudentGrade);
  };

  const fafsaField = () => {
    const propertyName = pn<AprUbResponseRow>("fafsa");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`44.${property?.displayName}`}>{apr.fafsa}</DataDisplay>, "participation", props.editFafsa, Permission.StudentFafsa);
  };

  const lastServiceDateField = () => {
    const propertyName = pn<AprUbResponseRow>("lastServiceDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`45.${property?.displayName}`}>{apr.lastServiceDate}</DataDisplay>, "participation", props.editServices, Permission.StudentService);
  };

  const reasonField = () => {
    const propertyName = pn<AprUbResponseRow>("reason");
    const property = properties.find((x) => x.propertyName === propertyName);

    return linkedField(<DataDisplay label={`46.${property?.displayName}`}>{apr.reason}</DataDisplay>, "participation", () => openEnrollmentEditor(), Permission.StudentEnrollment);
  };

  const workStudyField = () => {
    const propertyName = pn<AprUbResponseRow>("workStudy");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`47.${property?.displayName}`}>{apr.workStudy}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const employmentField = () => {
    const propertyName = pn<AprUbResponseRow>("employment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`48.${property?.displayName}`}>{apr.employment}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const culturalField = () => {
    const propertyName = pn<AprUbResponseRow>("cultural");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`49.${property?.displayName}`}>{apr.cultural}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const communityServiceField = () => {
    const propertyName = pn<AprUbResponseRow>("communityService");
    const property = properties.find((x) => x.propertyName === propertyName);

    return editableField(
      <DataDisplay label={`50.${property?.displayName}`}>{apr.communityService}</DataDisplay>,
      () => editEnrollment(),
      Permission.StudentEnrollment
    );
  };

  const postsecondaryEducationField = (
    fieldNumber: number,
    propertyName: keyof AprUbResponseRow,
    value: ReactNode
  ) => {
    const property = properties.find((x) => x.propertyName === propertyName);

    return <DataDisplay label={`${fieldNumber}.${property?.displayName}`}>{value}</DataDisplay>;
  };

  const postsecondaryValue = <T,>(storedValue: T | undefined, calculatedValue: T | undefined) =>
    storedValue ?? calculatedValue;

  const postsecondaryEducationEditor = () => {
    if (!postsecondaryEducationDraft) return null;

    const updateNumber = (propertyName: keyof PostsecondaryEducation, value: string) => {
      setPostsecondaryEducationDraft(produce(postsecondaryEducationDraft, draft => {
        (draft[propertyName] as number | undefined) = value === "" ? undefined : Number(value);
      }));
    };
    const updateText = (propertyName: keyof PostsecondaryEducation, value: string) => {
      setPostsecondaryEducationDraft(produce(postsecondaryEducationDraft, draft => {
        (draft[propertyName] as string | undefined) = value || undefined;
      }));
    };

    return (
      <Stack spacing="md" mb={3}>
        <Grid container spacing="sm">
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="55.PSECDFE" value={postsecondaryEducationDraft.psecdfe?.toString() ?? ""} onChange={e => updateNumber("psecdfe", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="56.PSEGradeLV" value={postsecondaryEducationDraft.pseGradeLV?.toString() ?? ""} onChange={e => updateNumber("pseGradeLV", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="57.CertificateCD" value={postsecondaryEducationDraft.certificateCD?.toString() ?? ""} onChange={e => updateNumber("certificateCD", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="58.CertificateDT" value={postsecondaryEducationDraft.certificateDT ?? ""} onChange={e => updateText("certificateDT", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="59.AssocDegreeCD" value={postsecondaryEducationDraft.assocDegreeCD?.toString() ?? ""} onChange={e => updateNumber("assocDegreeCD", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="60.AssocDegreeDT" value={postsecondaryEducationDraft.assocDegreeDT ?? ""} onChange={e => updateText("assocDegreeDT", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="61.BachDegreeCD" value={postsecondaryEducationDraft.bachDegreeCD?.toString() ?? ""} onChange={e => updateNumber("bachDegreeCD", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth label="62.BachDegreeDT" value={postsecondaryEducationDraft.bachDegreeDT ?? ""} onChange={e => updateText("bachDegreeDT", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="63.PSEEnrollObjNum" value={postsecondaryEducationDraft.pseEnrollObjNum?.toString() ?? ""} onChange={e => updateNumber("pseEnrollObjNum", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="64.PSRemediation" value={postsecondaryEducationDraft.psRemediation?.toString() ?? ""} onChange={e => updateNumber("psRemediation", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="65.PSCompleteNum" value={postsecondaryEducationDraft.psCompleteNum?.toString() ?? ""} onChange={e => updateNumber("psCompleteNum", e.target.value)} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth type="number" label="66.STEMDegree" value={postsecondaryEducationDraft.stemDegree?.toString() ?? ""} onChange={e => updateNumber("stemDegree", e.target.value)} /></Grid>
        </Grid>
        {editActions(savePostsecondaryEducation)}
      </Stack>
    );
  };

  const atRiskStdTestField = () => {
    return linkedField(<DataDisplay
      label="17.AtRiskStdTest"
    >{apr.atRiskStdTest}</DataDisplay>, "eligibility", () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const atRiskAlgebraField = () => {
    return linkedField(<DataDisplay
      label="19.AtRiskAlgebra"
    >{apr.atRiskAlgebra}</DataDisplay>, "eligibility", () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const atRiskLowGpaField = () => {
    return linkedField(<DataDisplay
      label="18.AtRiskLowGpa"
    >{apr.atRiskLowGPA}</DataDisplay>, "eligibility", () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const otherNeedField = () => {
    return editableField(<DataDisplay
      label="22.OtherNeed"
    >{apr.otherNeed}</DataDisplay>, () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const disconnectedField = () => {
    return linkedField(<DataDisplay
      label="21.Disconnected"
    >{apr.disconnected}</DataDisplay>, "eligibility", () => beginStudentEdit("eligibility"), Permission.Student);
  };

  const editActions = (save: () => void) => (
    <Stack direction="row" spacing="sm" justifyContent="flex-end">
      <Button variant="outlined" onClick={cancelEdit}>Cancel</Button>
      <Button variant="contained" onClick={save}>Save</Button>
    </Stack>
  );

  const demographicsEditor = () => (
    <Stack spacing="md" mb={3}>
      <Grid container spacing="sm">
        <Grid item xs={12} md={4}>
          <DatePicker label="Date of Birth" value={studentDraft.dateOfBirth} onChange={(value) => setStudentDraft(produce(studentDraft, draft => { draft.dateOfBirth = value; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="Gender" value={studentEditResource.genders?.find(x => x.id === studentDraft.genderId)} options={studentEditResource.genders} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.genderId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="Ethnicity" value={studentEditResource.ethnicities?.find(x => x.id === studentDraft.ethnicityId)} options={studentEditResource.ethnicities} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.ethnicityId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select label="Race" value={studentEditResource.races?.find(x => x.id === studentDraft.raceId)} options={studentEditResource.races} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.raceId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select multiple label="Additional Races" value={studentDraft.races ?? []} options={studentEditResource.races} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.races = value; }))} />
        </Grid>
      </Grid>
      {editActions(saveStudent)}
    </Stack>
  );

  const eligibilityEditor = () => (
    <Stack spacing="md" mb={3}>
      <Grid container spacing="sm">
        <Grid item xs={12} md={4}>
          <Select label="Eligibility" value={studentEditResource.eligibilities?.find(x => x.id === studentDraft.eligibilityId)} options={studentEditResource.eligibilities} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.eligibilityId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="Other Need" value={studentEditResource.otherNeeds?.find(x => x.id === studentDraft.otherNeedId)} options={studentEditResource.otherNeeds} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.otherNeedId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="At Risk - Standardized Test" value={studentEditResource.atRiskStdTests?.find(x => x.id === studentDraft.atRiskStdTestId)} options={studentEditResource.atRiskStdTests} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.atRiskStdTestId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="At Risk - Algebra" value={studentEditResource.atRiskAlgebras?.find(x => x.id === studentDraft.atRiskAlgebraId)} options={studentEditResource.atRiskAlgebras} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.atRiskAlgebraId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="At Risk - Low GPA" value={studentEditResource.atRiskLowGpas?.find(x => x.id === studentDraft.atRiskLowGpaId)} options={studentEditResource.atRiskLowGpas} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.atRiskLowGpaId = value?.id; }))} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Select label="Disconnected" value={BOOLEAN_SELECT_LIST_ITEMS.find(x => x.id === studentDraft.disconnected)} options={BOOLEAN_SELECT_LIST_ITEMS} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.disconnected = value?.id; }))} />
        </Grid>
      </Grid>
      {editActions(saveStudent)}
    </Stack>
  );

  const enrollmentEditor = (services: boolean) => {
    if (!enrollmentDraft) return null;
    const update = (change: (draft: StudentEnrollment) => void) => setEnrollmentDraft(produce(enrollmentDraft, change));
    const updateFirst = (change: (draft: StudentEnrollment) => void) => {
      if (!firstEnrollmentDraft) return;
      setFirstEnrollmentDraft(produce(firstEnrollmentDraft, change));
      if (firstEnrollmentDraft.id === enrollmentDraft.id) update(change);
    };
    return (
      <Stack spacing="md" mb={3}>
        <Grid container spacing="sm">
          {!services && <>
            <Grid item xs={12} md={4}><Select label="Grade Level" value={enrollmentEditResource.gradeLevels?.find(x => x.id === firstEnrollmentDraft?.gradeLevelId)} options={enrollmentEditResource.gradeLevels} getOptionLabel={x => x.name} onChange={(e, value) => updateFirst(draft => { draft.gradeLevelId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Participant Status" value={enrollmentEditResource.participantStatuses?.find(x => x.id === firstEnrollmentDraft?.participantStatusId)} options={enrollmentEditResource.participantStatuses} getOptionLabel={x => x.name} onChange={(e, value) => updateFirst(draft => { draft.participantStatusId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Participant Level" value={enrollmentEditResource.participantLevels?.find(x => x.id === enrollmentDraft.participantLevelId)} options={enrollmentEditResource.participantLevels} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.participantLevelId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Dual Enrollment Participation" value={enrollmentEditResource.dualEnrollmentParticipations?.find(x => x.id === enrollmentDraft.dualEnrollmentParticipationId)} options={enrollmentEditResource.dualEnrollmentParticipations} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.dualEnrollmentParticipationId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Dual Enrollment Credentials" value={enrollmentEditResource.dualEnrollmentCredentials?.find(x => x.id === enrollmentDraft.dualEnrollmentCredentialsId)} options={enrollmentEditResource.dualEnrollmentCredentials} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.dualEnrollmentCredentialsId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Deceased" value={studentEditResource.deceased?.find(x => x.id === studentDraft.deceasedId)} options={studentEditResource.deceased} getOptionLabel={x => x.name} onChange={(e, value) => setStudentDraft(produce(studentDraft, draft => { draft.deceasedId = value?.id; }))} /></Grid>
          </>}
          {services && <>
            <Grid item xs={12} md={4}><Select label="LEP Instruction" value={enrollmentEditResource.workStudyCulturalCommunity?.find(x => x.id === enrollmentDraft.lepInstructionId)} options={enrollmentEditResource.workStudyCulturalCommunity} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.lepInstructionId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Work Study" value={enrollmentEditResource.workStudyCulturalCommunity?.find(x => x.id === enrollmentDraft.workStudyId)} options={enrollmentEditResource.workStudyCulturalCommunity} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.workStudyId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Employment" value={enrollmentEditResource.employments?.find(x => x.id === enrollmentDraft.employmentUBId)} options={enrollmentEditResource.employments} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.employmentUBId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Cultural Activities" value={enrollmentEditResource.workStudyCulturalCommunity?.find(x => x.id === enrollmentDraft.culturalId)} options={enrollmentEditResource.workStudyCulturalCommunity} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.culturalId = value?.id; })} /></Grid>
            <Grid item xs={12} md={4}><Select label="Community Service" value={enrollmentEditResource.workStudyCulturalCommunity?.find(x => x.id === enrollmentDraft.communityServiceId)} options={enrollmentEditResource.workStudyCulturalCommunity} getOptionLabel={x => x.name} onChange={(e, value) => update(draft => { draft.communityServiceId = value?.id; })} /></Grid>
          </>}
        </Grid>
        {editActions(saveEnrollment)}
      </Stack>
    );
  };

  // const schoolYearSelect = () => {
  //   return (
  //     <Select
  //       label="School Year"
  //       value={selectedSchoolyear}
  //       options={schoolYears}
  //       onChange={(e, value) => setSelectedSchoolyear(value)}
  //       getOptionLabel={(year: number) => formatSchoolYear(year)!}
  //       renderOption={(props, year) => <li {...props}>{formatSchoolYear(year)}</li>}
  //     />
  //   );
  // };

  const schoolYearSelect = () => {
    const selectedValue =
      schoolYears.find((x) => x === selectedSchoolyear) ?? undefined;

    return (
      <Box pt={1}>
        <Grid container spacing="md" alignItems="center">
          <Grid item xs={12} md={2}>
            <Select
              label="School Year"
              required
              value={selectedValue}
              options={schoolYears}
              getOptionLabel={(year: number) => formatSchoolYear(year)!}
              renderOption={(props, year: number) => {
                return (
                  <li {...props}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                      sx={{
                        fontWeight: year === selectedSchoolyear ? 600 : 400,
                        color:
                          year === selectedSchoolyear
                            ? "primary.main"
                            : "text.primary",
                      }}
                    >
                      {formatSchoolYear(year)}

                      {year === selectedSchoolyear && (
                        <Box
                          sx={{
                            ml: 1,
                            px: 1,
                            borderRadius: "8px",
                            fontSize: "0.7rem",
                            color: "primary.main",
                          }}
                        >
                          {/* <SpellcheckIcon fontSize="small" /> */}
                        </Box>
                      )}
                    </Box>
                  </li>
                );
              }}
              onChange={(e, value) =>
                setSelectedSchoolyear(value ?? undefined)
              }
              size="small"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // const enrollmentParticipantStatus = () => {
  //   const enrollment = apr.enrollments?.find((x) => x.schoolYear === selectedSchoolyear);
  //   return <DataDisplay label="Participant Status">{enrollment?.participantStatus}</DataDisplay>;
  // };

  //  

  return (
    <Card>
      <Grid sx={{ pb: 2 }}>
        {schoolYearSelect()}
      </Grid>
      <Grid container spacing="md">
        <Grid
          item
          xs={12}
          lg={12}
          sx={{
            pr: { md: "10px", xs: 0 },
            pb: { xs: "10px", md: 0 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              border: "1px solid #ddd",
              overflow: "hidden",
              background: "#fff",
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                background: "#0d57a1",
                color: "white",
                px: 2,
                py: 1,
                fontSize: "20px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Participant's Demographic Information</span>
              {isAuthorized(Permission.Student, PermissionAction.Update) && (
                <IconButton color="inherit" size="small" aria-label="Edit participant demographics" onClick={() => beginStudentEdit("demographics")}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            <Box sx={{ p: 3 }}>
              {editingSection === "demographics" && demographicsEditor()}
              <Grid container spacing="sm">

                <Grid item xs={12} md={4}>
                  {dateOfBirthField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {genderCodeField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {ethnicityField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceNativeField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceAsianField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceAfricanField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRaceWhiteField()}
                </Grid>

                <Grid item xs={12} md={4}>
                  {isRacePacificField()}
                </Grid>

              </Grid>
            </Box>

          </Box>
        </Grid>
        {/* Eligibility and Initial Selection SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Eligibility and Initial Selection</span>
                {isAuthorized(Permission.Student, PermissionAction.Update) && (
                  <IconButton color="inherit" size="small" aria-label="Edit eligibility and initial selection" onClick={() => beginStudentEdit("eligibility")}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                {editingSection === "eligibility" && eligibilityEditor()}
                <Grid container spacing="sm">
                  {/* <Grid item xs={12} md={6}>
                    {schoolYearSelect()}
                  </Grid> */}

                  {/* <Grid item xs={12} md={4}>
                    {enrollmentParticipantStatus()}
                  </Grid> */}

                  <Grid item xs={12} md={4}>
                    {eligibilityField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {atRiskStdTestField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {atRiskLowGpaField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {atRiskAlgebraField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {lepEntryField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {disconnectedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {otherNeedField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

       

        {/* Participation and Status in secondary school SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Participation and Status in Secondary School</span>
                {isAuthorized(Permission.StudentEnrollment, PermissionAction.Update) && (
                  <IconButton color="inherit" size="small" aria-label="Edit participation and status" onClick={() => beginEnrollmentEdit("participation")}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ p: 3 }}>
                {editingSection === "participation" && enrollmentEditor(false)}
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {ncesSchoolField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {projectEntryDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {collegeGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {deceasedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {participantStatusField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {participantLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {federalServedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {startGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {endGradeLevelField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGPAField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGPAUnweightedField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGraduateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {highSchoolGraduateDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {rigorousStudyField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {hsProficientRlaField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {hsProficientMathField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {apCourseField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {dualEnrollParticipationField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {dualEnrollCredentials()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {advancedMathField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {fafsaField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {lastServiceDateField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {reasonField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

         {/* Project Services SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Project Services</span>
                {isAuthorized(Permission.StudentEnrollment, PermissionAction.Update) && (
                  <IconButton color="inherit" size="small" aria-label="Edit project services" onClick={() => beginEnrollmentEdit("services")}>
                    <EditIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                {editingSection === "services" && enrollmentEditor(true)}
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {lepInstructionField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {workStudyField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {employmentField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {culturalField()}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    {communityServiceField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

        {/* Postsecondary education SECTION */}
        <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Postsecondary Education</span>
                {isAuthorized(Permission.StudentNscCollege, PermissionAction.Update) && (
                  <IconButton
                    color="inherit"
                    size="small"
                    aria-label="Edit postsecondary education"
                    onClick={beginPostsecondaryEducationEdit}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ p: 3 }}>
                {editingSection === "postsecondary" && postsecondaryEducationEditor()}
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    <Stack direction="row" alignItems="center" spacing="xs">
                      <Box flexGrow={1}>{postsecondaryEducationField(52, "selfTranCD", apr.selfTranCD)}</Box>
                      {isAuthorized(Permission.StudentNscCollege, PermissionAction.Update) && (
                        <IconButton size="small" aria-label="Edit SelfTranCD source information" onClick={props.editPostSecondary}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={4}>{postsecondaryEducationField(55, "pseCdfe", postsecondaryValue(postsecondaryEducation?.psecdfe?.toString(), apr.pseCdfe))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(56, "pseGradeLv", postsecondaryValue(postsecondaryEducation?.pseGradeLV, apr.pseGradeLv))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(57, "certificateCd", postsecondaryValue(postsecondaryEducation?.certificateCD, apr.certificateCd))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(58, "certificateDt", postsecondaryValue(postsecondaryEducation?.certificateDT, apr.certificateDt))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(59, "assocDegreeCd", postsecondaryValue(postsecondaryEducation?.assocDegreeCD, apr.assocDegreeCd))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(60, "assocDegreeDt", postsecondaryValue(postsecondaryEducation?.assocDegreeDT, apr.assocDegreeDt))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(61, "bachDegreeCd", postsecondaryValue(postsecondaryEducation?.bachDegreeCD, apr.bachDegreeCd))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(62, "bachDegreeDt", postsecondaryValue(postsecondaryEducation?.bachDegreeDT, apr.bachDegreeDt))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(63, "pseEnrollObjNum", postsecondaryValue(postsecondaryEducation?.pseEnrollObjNum, apr.pseEnrollObjNum))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(64, "psRemediation", postsecondaryValue(postsecondaryEducation?.psRemediation, apr.psRemediation))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(65, "psCompleteNum", postsecondaryValue(postsecondaryEducation?.psCompleteNum, apr.psCompleteNum))}</Grid>
                  <Grid item xs={12} md={4}>{postsecondaryEducationField(66, "stemDegree", postsecondaryValue(postsecondaryEducation?.stemDegree, apr.stemDegree))}</Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid>

        {/* FOR FUTURE USE SECTION */}
        {/* <Grid item xs={12} lg={12} sx={{
          pr: { md: "10px", xs: 0 },
          pb: { xs: "10px", md: 0 },
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack direction="column" sx={{ gap: 2 }}>
            <Box
              sx={{
                borderRadius: 3,
                border: "1px solid #ddd",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  background: "#0d57a1",
                  color: "white",
                  px: 2,
                  py: 1,
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                FUTURE USE
              </Box>
                            <Box sx={{ p: 3 }}>
                <Grid container spacing="sm">

                  <Grid item xs={12} md={4}>
                    {lepInstructionField()}
                  </Grid>

                </Grid>
              </Box>
            </Box>
          </Stack>
        </Grid> */}

      </Grid>
      {(matchTemplatePath(studentEnrollmentPageRoute.templatePath) || matchTemplatePath(schoolPageRoute.templatePath)) && <Outlet />}
    </Card>
  )
};
